import type { ComponentDefinitionMap } from '@app/shared/vue/global-components/types'

import { createApp } from '@app/adapters/create-app'
import { isDOM } from '@app/shared/predicates'

const mountOnDom =
  (onDomLoad: typeof window.onDomLoad) =>
  (componentDefinitionMap: ComponentDefinitionMap): ComponentDefinitionMap => {
    const componentKeys = [...componentDefinitionMap.keys()]
    componentKeys.forEach((key) => {
      onDomLoad(function ($dom) {
        const doc = $dom[0]

        if (!isDOM(doc)) {
          return
        }

        doc.querySelectorAll(key).forEach((element) => {
          const el = element as HTMLElement
          const componentDefinition = componentDefinitionMap.get(key)

          if (!componentDefinition) {
            return
          }

          const props = Object.keys(el.dataset).reduce((acc, key) => {
            const value = el.dataset[key]

            if (value && typeof value === 'string') {
              try {
                const val = JSON.parse(value) as unknown
                acc[key] = val
              } catch {
                acc[key] = el.dataset[key]
              }
            } else {
              acc[key] = el.dataset[key]
            }

            return acc
          }, {} as Record<string, unknown>)

          const app = createApp(
            componentDefinition.component,
            {
              ...props,
            },
            componentDefinition.meta || {}
          )

          const vuexStore =
            typeof componentDefinition?.createStore === 'function'
              ? componentDefinition.createStore(el.dataset)
              : null

          if (vuexStore) {
            app.use(vuexStore)
          }

          app.mount(el)
        })
      })
    })

    return componentDefinitionMap
  }

/**
 * Readies a global mounter
 * @param {Function} onDomLoad - an abstraction providing access to the document when it's ready
 * @returns { GlobalMounter } - The global mounter instance that accepts the list of components
 */
export const globalMounter = (
  onDomLoad: typeof window.onDomLoad
): ((
  componentDefinitionMap: ComponentDefinitionMap
) => ComponentDefinitionMap) => {
  return mountOnDom(onDomLoad)
}

/**
 * Registers the given list of components globally and mounts them on the dom if encountered
 * @callback GlobalMounter - globalMounterCallback
 * @param {ComponentMap} - The components to register globally and mount on the dom, provided as a Map
 */

/**
 * A map containing keys / vue components
 * @typedef {Map<String, Function>} ComponentMap
 */
